import * as React from 'react';

import type {FocusPlace, FocusRecovery} from '../focusRecovery/context';
import {FocusRecoveryContext} from '../focusRecovery/context';
import {focusStatic} from '../helpers/focusStatic';
import {getFirstField, getTabbable} from '../helpers/tabbable';

// A modal dialog is isolated from the page while it is open: the rest of the
// page is inert, so neither the Tab key nor a screen reader can reach it, and
// the dialog gets focus when it opens and gives it back when it closes. Stage
// (alert, confirm and custom boxes), ModalLayer and the quick switcher all go
// through this hook by way of `IsolatedModal`, so every modal dialog in the
// dashboard, the site builder and the tip jar behaves the same way.

type OpenDialog = {
  container: HTMLElement;
  // Read when Escape is pressed, so a new callback on each render is fine.
  onEscape: React.RefObject<(() => void) | null | undefined>;
  // Elements that this dialog made inert.
  inerted: Array<Element>;
};

// Open dialogs, the top one last. Only the top one is live.
const stack: Array<OpenDialog> = [];

// How many open dialogs made each element inert. An element leaves the map,
// and loses `inert`, when the last of them closes. Elements that were inert
// before any dialog opened never enter it, and are left as they were.
const inertCounts = new Map<Element, number>();

type Opener = {element: Element | null; place: FocusPlace | null};

// For a dialog that has closed, the element it gave focus back to, and where
// that element was. A dialog opened from inside another one that has since
// closed takes its focus target from here, so focus does not end up on a
// button that is gone.
const closedOpeners = new WeakMap<Element, Opener>();

/**
 * The open dialog that holds `element`, if there is one. That is so for an
 * element that has left the document too, when it went with the dialog, until
 * the dialog's cleanup has run.
 */
export const dialogContaining = (element: Element): HTMLElement | null =>
  stack.find(({container}) => container.contains(element))?.container ?? null;

const makeOthersInert = (container: HTMLElement): Array<Element> => {
  const inerted: Array<Element> = [];
  for (
    let node: Element = container;
    node.parentElement && node !== document.body;
    node = node.parentElement
  ) {
    for (const sibling of Array.from(node.parentElement.children)) {
      if (
        sibling === node ||
        /^(SCRIPT|STYLE|TEMPLATE)$/.test(sibling.tagName)
      ) {
        continue;
      }
      const count = inertCounts.get(sibling);
      if (count === undefined && sibling.hasAttribute('inert')) {
        continue;
      }
      inertCounts.set(sibling, (count ?? 0) + 1);
      sibling.setAttribute('inert', '');
      inerted.push(sibling);
    }
  }
  return inerted;
};

const release = (inerted: Array<Element>) => {
  for (const element of inerted) {
    const count = (inertCounts.get(element) ?? 1) - 1;
    if (count > 0) {
      inertCounts.set(element, count);
    } else {
      inertCounts.delete(element);
      element.removeAttribute('inert');
    }
  }
};

// Focuses an element that need not take focus by itself (`focusStatic`). The
// page does not scroll: a fallback target such as the page heading can be far
// from where the person was.
const focusInPlace = (element: HTMLElement): boolean =>
  focusStatic(element, {preventScroll: true});

const HEADINGS = 'h1, h2, h3, h4, h5, h6, [role="heading"]';

// The dialog's heading, or the dialog itself when it has none.
const focusHeading = (container: HTMLElement) => {
  for (const selector of [HEADINGS, '[role="dialog"]']) {
    const target = container.querySelector<HTMLElement>(selector);
    if (target && focusInPlace(target)) {
      return;
    }
  }
  focusInPlace(getTabbable(container)[0] ?? container);
};

const focusInitial = (container: HTMLElement) => {
  const field = getFirstField(container);
  if (field) {
    field.focus();
  } else {
    focusHeading(container);
  }
};

// When the opener is gone, and Stage has no place for focus either, focus goes
// to the heading of whatever is left: the dialog below, when dialogs are
// nested, or else the page. It never goes to <body>, where a screen reader
// would start again from the top of the page.
export const focusFallback = () => {
  const top = stack[stack.length - 1];
  if (top) {
    focusHeading(top.container);
    return;
  }
  const candidates = [
    ...Array.from(document.querySelectorAll<HTMLElement>('main h1')),
    ...Array.from(document.querySelectorAll<HTMLElement>('h1')),
    ...Array.from(document.querySelectorAll<HTMLElement>(HEADINGS)),
    ...Array.from(document.querySelectorAll<HTMLElement>('main')),
    ...getTabbable(document.body),
  ];
  for (const candidate of candidates) {
    if (!candidate.closest('[inert], [hidden]') && focusInPlace(candidate)) {
      return;
    }
  }
};

// The element to give focus back to. An opener inside a dialog that has since
// closed is replaced with that dialog's own opener. When the opener is gone,
// the element is null, and the place is where the opener was.
const resolveOpener = (opener: Opener): Opener => {
  let current = opener;
  while (current.element && !current.element.isConnected) {
    let owner: Element | null = current.element;
    while (owner && !closedOpeners.has(owner)) {
      owner = owner.parentElement;
    }
    if (!owner) {
      return {element: null, place: current.place};
    }
    current = closedOpeners.get(owner)!;
  }
  return current;
};

// Where the caret is in an opener that is an editable region, such as a rich
// text editor. `focus()` alone puts the caret at the start of it.
const caretIn = (opener: Element | null): Range | null => {
  const selection = document.getSelection();
  if (
    !opener?.matches('[contenteditable]:not([contenteditable="false"])') ||
    !selection?.rangeCount
  ) {
    return null;
  }
  const range = selection.getRangeAt(0);
  return opener.contains(range.commonAncestorContainer)
    ? range.cloneRange()
    : null;
};

const restoreFocus = (
  container: HTMLElement,
  opener: Opener,
  caret: Range | null,
  recovery: FocusRecovery | null,
) => {
  const resolved = resolveOpener(opener);
  closedOpeners.set(container, resolved);
  const target = resolved.element;

  // Leave focus alone when it has already gone somewhere else on purpose, such
  // as into another dialog.
  const active = document.activeElement;
  if (
    active &&
    active !== document.body &&
    active.isConnected &&
    !container.contains(active)
  ) {
    return;
  }

  if (target instanceof HTMLElement || target instanceof SVGElement) {
    if (
      caret &&
      target === opener.element &&
      caret.startContainer.isConnected &&
      caret.endContainer.isConnected
    ) {
      const selection = document.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(caret);
    }
    target.focus();
    if (document.activeElement === target) {
      return;
    }
  }
  // The opener is gone, as when the dialog deleted its row. Focus recovery
  // decides where focus goes then, as it does for every other deletion (see
  // `FocusRecoveryProvider`).
  if (!recovery?.recover(resolved.place)) {
    focusFallback();
  }
};

const onKeyDown = (event: KeyboardEvent) => {
  if (
    (event.key !== 'Escape' && event.key !== 'Esc') ||
    event.defaultPrevented ||
    event.isComposing
  ) {
    return;
  }
  const onEscape = stack[stack.length - 1]?.onEscape.current;
  if (onEscape) {
    event.preventDefault();
    onEscape();
  }
};

/**
 * Isolates a modal dialog from the rest of the page while it is mounted. Put
 * the returned ref on the element that holds the whole dialog, backdrop
 * included, and mount the component only while the dialog is open.
 *
 * - On open, it stores the focused element, makes everything outside the
 *   dialog `inert`, and moves focus to the first field in the dialog, or to
 *   its heading when it has no field.
 * - Escape calls `onEscape` (on the top dialog only, when dialogs are nested).
 *   Leave `onEscape` out for a dialog that must not close; that dialog still
 *   needs a visible way to finish it.
 * - On close, by any means, the page is live again and focus goes back to the
 *   stored element. If that element is gone, focus recovery moves focus to the
 *   element that took its place (see `FocusRecoveryProvider`), or else focus
 *   goes to the page heading. In an editable region, the caret goes back where
 *   it was.
 */
export const useDialogIsolation = <T extends HTMLElement = HTMLDivElement>({
  onEscape,
}: {
  onEscape?: (() => void) | null;
}): React.RefObject<T | null> => {
  const ref = React.useRef<T | null>(null);
  const recovery = React.useContext(FocusRecoveryContext);

  // The opener is read while rendering: once the dialog's content mounts,
  // something inside it may already have taken focus. Its place is read then
  // too, while it is still in the document: a dialog can delete the row that
  // holds it.
  const [{caret, opener}] = React.useState<{
    caret: Range | null;
    opener: Opener;
  }>(() => {
    if (
      typeof document === 'undefined' ||
      !document.activeElement ||
      document.activeElement === document.body
    ) {
      return {caret: null, opener: {element: null, place: null}};
    }
    const active = document.activeElement;
    return {
      caret: caretIn(active),
      opener: {element: active, place: recovery?.placeOf(active) ?? null},
    };
  });

  const escape = React.useRef(onEscape);
  // Read when the dialog closes.
  const recoveryRef = React.useRef(recovery);
  React.useEffect(() => {
    escape.current = onEscape;
    recoveryRef.current = recovery;
  });

  React.useEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }
    if (!stack.length) {
      document.addEventListener('keydown', onKeyDown);
    }
    const entry: OpenDialog = {
      container,
      onEscape: escape,
      inerted: makeOthersInert(container),
    };
    stack.push(entry);
    if (!container.contains(document.activeElement)) {
      focusInitial(container);
    }

    return () => {
      stack.splice(stack.indexOf(entry), 1);
      if (!stack.length) {
        document.removeEventListener('keydown', onKeyDown);
      }
      release(entry.inerted);
      restoreFocus(container, opener, caret, recoveryRef.current);
    };
  }, [caret, opener]);

  return ref;
};
